import { useState, useRef, useEffect } from 'react';
import { Image, Calendar, MoreVertical, Check, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

interface PropertyCardProps {
  address: string;
  photoCount: number;
  date: string;
  imageUrl: string;
  thumbnails?: string[];
  onCardClick?: () => void;
  onRename?: (newName: string) => void;
  onDeleteClick?: () => void;
}

export function PropertyCard({
  address,
  photoCount,
  date,
  imageUrl,
  thumbnails,
  onCardClick,
  onRename,
  onDeleteClick
}: PropertyCardProps) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [editedName, setEditedName] = useState(address);
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if property is empty (no photos)
  const isEmpty = photoCount === 0;

  // Focus input when entering rename mode
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditedName(address);
    setIsRenaming(true);
  };

  const handleConfirmRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (editedName.trim() && editedName !== address) {
      onRename?.(editedName.trim());
    }
    setIsRenaming(false);
  };

  const handleCancelRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditedName(address);
    setIsRenaming(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && editedName.trim()) {
      if (editedName !== address) {
        onRename?.(editedName.trim());
      }
      setIsRenaming(false);
    } else if (e.key === 'Escape') {
      setEditedName(address);
      setIsRenaming(false);
    }
  };

  // Default thumbnails if not provided
  const defaultThumbnails = [
    'https://images.unsplash.com/photo-1595515106705-257fa2d62381?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBiYXRocm9vbSUyMGludGVyaW9yfGVufDF8fHx8MTc2Njk0MDQ1OHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    'https://images.unsplash.com/photo-1680210849773-f97a41c6b7ed?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb250ZW1wb3JhcnklMjBraXRjaGVuJTIwZGVzaWdufGVufDF8fHx8MTc2NzAyODEyNXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbGl2aW5nJTIwcm9vbXxlbnwxfHx8fDE3NjY5ODA4NTR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
  ];

  const displayThumbnails = thumbnails || defaultThumbnails;

  if (isEmpty) {
    // Empty State - Inset Design
    return (
      <div
        className="group relative cursor-pointer bg-card rounded-[24px] overflow-hidden transition-all duration-300 shadow-[0_8px_28px_rgba(0,0,0,0.06)] hover:-translate-y-1 border border-border/40 flex flex-col h-[320px] w-full p-4"
        onClick={onCardClick}
      >
        {/* Image Grid Area - Inset with rounded corners */}
        <div className="relative w-full h-[220px] overflow-hidden bg-secondary/30 rounded-[16px] flex items-center justify-center">
          <Image className="w-12 h-12 text-muted-foreground/20" strokeWidth={1} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-background/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-[12px] font-medium shadow-sm border border-border/50">
              Brak zdjęć
            </div>
          </div>
        </div>

        {/* Card Body */}
        <div className="pt-4 flex flex-col justify-between flex-1">
          {/* Title - Address or Rename Input */}
          <div className="mb-1">
            {isRenaming ? (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <input
                  ref={inputRef}
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 text-[16px] font-semibold bg-transparent border-b border-primary/20 px-0 py-0.5 outline-none text-primary w-full"
                />
                <button
                  onClick={handleConfirmRename}
                  className="flex items-center justify-center w-6 h-6 rounded-md bg-green-100 hover:bg-green-200"
                  disabled={!editedName.trim()}
                >
                  <Check className="w-3 h-3 text-green-700" strokeWidth={2.5} />
                </button>
                <button
                  onClick={handleCancelRename}
                  className="flex items-center justify-center w-6 h-6 rounded-md bg-red-100 hover:bg-red-200"
                >
                  <X className="w-3 h-3 text-red-700" strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <h3 className="text-[18px] font-semibold leading-tight text-primary tracking-tight font-clash truncate" title={address} style={{ fontFamily: 'Clash Grotesk, sans-serif' }}>
                {address}
              </h3>
            )}
          </div>

          {/* Meta Data Row with More Options */}
          <div className="flex items-center justify-between mt-auto">
            <p className="text-[13px] text-muted-foreground font-medium">
              Brak danych
            </p>

            {/* More Options Button - Bottom Right */}
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground opacity-100 lg:opacity-0 lg:group-hover:opacity-100">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={handleRenameClick}
                  >
                    <span>Edytuj nazwę</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-700 dark:focus:text-red-300 focus:bg-red-50 dark:focus:bg-red-900/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteClick?.();
                    }}
                  >
                    <span>Usuń nieruchomość</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal State - Grid of Thumbnails
  // Prepare grid images (up to 4)
  return (
    <div
      className="group relative cursor-pointer bg-card rounded-[24px] overflow-hidden transition-all duration-300 shadow-[0_8px_28px_rgba(0,0,0,0.06)] hover:-translate-y-1 border border-border/40 flex flex-col h-[320px] w-full p-4"
      onClick={onCardClick}
    >
      {/* Image Grid Area - Inset with rounded corners */}
      <div className="relative w-full h-[220px] overflow-hidden bg-secondary/30 rounded-[16px] flex items-center justify-center">
        {(() => {
          // Flatten all images into one array
          const allImages = [imageUrl, ...(thumbnails || [])].filter(Boolean).slice(0, 4);
          const count = allImages.length;

          // Case: 1 Image (Full)
          if (count <= 1) {
            return (
              <img
                src={imageUrl}
                alt={address}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            );
          }

          // Case: 2 Images (Side by Side)
          if (count === 2) {
            return (
              <div className="w-full h-full grid grid-cols-2 gap-[2px]">
                {allImages.map((img, idx) => (
                  <div key={idx} className="relative w-full h-full overflow-hidden">
                    <img
                      src={img}
                      alt={`${address}-${idx}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                ))}
              </div>
            );
          }

          // Case: 3 Images (Grid with 1 Placeholder)
          if (count === 3) {
            return (
              <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-[2px]">
                {allImages.map((img, idx) => (
                  <div key={idx} className="relative w-full h-full overflow-hidden">
                    <img
                      src={img}
                      alt={`${address}-${idx}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                ))}
                {/* 4th Slot Placeholder */}
                <div className="flex items-center justify-center bg-muted/50">
                  <div className="w-8 h-8 rounded-full bg-background/50 flex items-center justify-center">
                    <Image className="w-4 h-4 text-muted-foreground/50" />
                  </div>
                </div>
              </div>
            );
          }

          // Case: 4+ Images (Standard 2x2 Grid)
          return (
            <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-[2px]">
              {allImages.map((img, idx) => (
                <div key={idx} className="relative w-full h-full overflow-hidden">
                  <img
                    src={img}
                    alt={`${address}-${idx}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Card Body */}
      < div className="pt-4 flex flex-col justify-between flex-1" >
        {/* Title */}
        < div className="mb-1" >
          {
            isRenaming ? (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <input
                  ref={inputRef}
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 text-[16px] font-semibold bg-transparent border-b border-primary/20 px-0 py-0.5 outline-none text-primary w-full"
                />
                <button
                  onClick={handleConfirmRename}
                  className="flex items-center justify-center w-6 h-6 rounded-md bg-green-100 hover:bg-green-200"
                >
                  <Check className="w-3 h-3 text-green-700" strokeWidth={2.5} />
                </button>
                <button
                  onClick={handleCancelRename}
                  className="flex items-center justify-center w-6 h-6 rounded-md bg-red-100 hover:bg-red-200"
                >
                  <X className="w-3 h-3 text-red-700" strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <h3 className="text-[18px] font-semibold leading-tight text-primary tracking-tight font-clash truncate" title={address} style={{ fontFamily: 'Clash Grotesk, sans-serif' }}>
                {address}
              </h3>
            )}
        </div>

        {/* Meta Data */}
        <div className="flex items-center justify-between mt-auto">
          <p className="text-[13px] text-muted-foreground font-medium">
            Utworzono: {date}
          </p>

          {/* More Options Trigger */}
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground opacity-100 lg:opacity-0 lg:group-hover:opacity-100">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleRenameClick}>Edytuj nazwę</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600 dark:text-red-400 focus:text-red-700 dark:focus:text-red-300 focus:bg-red-50 dark:focus:bg-red-900/10" onClick={(e) => { e.stopPropagation(); onDeleteClick?.(); }}>
                  Usuń nieruchomość
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div >
    </div >
  );
}